/**
 * /workstreams: My workstreams split by the viewer's role on each —
 * DRI, then Exec sponsor, then Working team (every other role, and workstreams
 * you're only on through a metric you own). The server sends the role on each
 * my_workstreams nav node (`my_role`); a workstream sits under its highest one.
 * A searched person's workstreams split the same way, by their role.
 */
import {isSameLdap} from 'views/workstreams/detail/scopeRouting';

export const MY_ROLE_GROUPS = [
  {key: 'dri', label: 'DRI'},
  {key: 'exec_sponsor', label: 'Exec sponsor'},
  {key: 'working_team', label: 'Working team'},
] as const;

export type MyRole = (typeof MY_ROLE_GROUPS)[number]['key'];

const toRole = (role?: string | null): MyRole =>
  MY_ROLE_GROUPS.find((group) => group.key === role)?.key ?? 'working_team';

/**
 * A searched person's role on a workstream, from its people: their highest of
 * DRI, Exec sponsor; anything else (or not listed) is Working team.
 */
export const personRole = (
  associations: Array<{dri: string; employee: {ldap: string}}> | undefined,
  ldap: string
): MyRole => {
  const roles = new Set(
    associations?.filter(({employee}) => isSameLdap(employee.ldap, ldap)).map(({dri}) => dri)
  );
  return MY_ROLE_GROUPS.find(({key}) => roles.has(key))?.key ?? 'working_team';
};

/**
 * Items under each role, in role order, empty roles left out. `roleOf` reads
 * an item's role (unknown → Working team); order within a role is kept.
 */
export const groupByMyRole = <T>(
  items: T[],
  roleOf: (item: T) => string | null | undefined
): Array<{key: MyRole; label: string; items: T[]}> =>
  MY_ROLE_GROUPS.map(({key, label}) => ({
    key,
    label,
    items: items.filter((item) => toRole(roleOf(item)) === key),
  })).filter((group) => group.items.length > 0);
